/**
 * PremiumIconBar.jsx
 * 2ECOMP: PremiumUI Refresh — Menu lateral colapsável (igual GPSWOX 4)
 *
 * - Expandido (170px): ícone + texto + logo + chevron no Objects
 * - Colapsado (50px): só ícones + tooltips + hamburger centralizado
 * - 12 itens de navegação principal + 2 no rodapé
 * - Seletor de idioma no menu Account
 * - Header com logo + botão hamburger
 */

import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  InputAdornment,
} from '@mui/material';
import ReactCountryFlag from 'react-country-flag';
import { makeStyles } from 'tss-react/mui';
import { useDispatch, useSelector } from 'react-redux';

// 2ECOMP: PremiumUI Refresh — ícones MUI
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';
import NotificationsOutlined from '@mui/icons-material/NotificationsOutlined';
import HistoryOutlined from '@mui/icons-material/HistoryOutlined';
import AssessmentOutlined from '@mui/icons-material/AssessmentOutlined';
import SendOutlined from '@mui/icons-material/SendOutlined';
import VideocamOutlined from '@mui/icons-material/VideocamOutlined';
import BuildOutlined from '@mui/icons-material/BuildOutlined';
import PeopleOutlined from '@mui/icons-material/PeopleOutlined';
import HexagonOutlined from '@mui/icons-material/HexagonOutlined';
import RouteOutlined from '@mui/icons-material/RouteOutlined';
import PlaceOutlined from '@mui/icons-material/PlaceOutlined';
import DashboardOutlined from '@mui/icons-material/DashboardOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import AccountCircleOutlined from '@mui/icons-material/AccountCircleOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';

import { useLocalization, useTranslation } from '../../common/components/LocalizationProvider';
import { sessionActions } from '../../store';
import LogoImage from '../../common/auth/LogoImage';
import fetchOrThrow from '../../common/util/fetchOrThrow';
import {
  ICON_BAR_WIDTH,
  ICON_BAR_COLLAPSED_WIDTH,
  Z_INDEX,
  SELECTION_TRANSITION,
  SIDEBAR_TRANSITION,
} from '../../common/theme/layout';

// 2ECOMP: PremiumUI Refresh — 12 itens de navegação principal
const NAV_ITEMS = [
  {
    id: 'objects', icon: LocationOnOutlined, labelKey: 'deviceTitle', label: 'Dispositivos', action: 'toggle',
  },
  {
    id: 'alerts', icon: NotificationsOutlined, labelKey: 'reportEvents', label: 'Alertas', route: '/reports/events',
  },
  {
    id: 'history', icon: HistoryOutlined, labelKey: 'reportReplay', label: 'Histórico', route: '/replay',
  },
  {
    id: 'reports', icon: AssessmentOutlined, labelKey: 'reportTitle', label: 'Relatórios', route: '/reports/combined',
  },
  {
    id: 'commands', icon: SendOutlined, labelKey: 'commandTitle', label: 'Comandos', action: 'modal',
  },
  {
    id: 'camera', icon: VideocamOutlined, labelKey: 'sharedCamera', label: 'Câmeras', route: '/camera',
  },
  {
    id: 'maintenance', icon: BuildOutlined, labelKey: 'sharedMaintenance', label: 'Manutenção', route: '/settings/maintenances',
  },
  {
    id: 'drivers', icon: PeopleOutlined, labelKey: 'sharedDrivers', label: 'Motoristas', route: '/settings/drivers',
  },
  {
    id: 'geofencing', icon: HexagonOutlined, labelKey: 'sharedGeofences', label: 'Geocercas', route: '/geofences',
  },
  {
    id: 'routes', icon: RouteOutlined, labelKey: 'sharedRoutes', label: 'Rotas', route: '/settings/route-planning',
  },
  {
    id: 'poi', icon: PlaceOutlined, labelKey: 'poiTitle', label: 'POI', route: '/settings/pois',
  },
  {
    id: 'overview', icon: DashboardOutlined, labelKey: 'sharedOverview', label: 'Visão Geral', route: '/overview',
  },
];

// 2ECOMP: PremiumUI Refresh — 3 itens no rodapé
const BOTTOM_ITEMS = [
  {
    id: 'settings', icon: SettingsOutlined, labelKey: 'sharedPreferences', label: 'Configurações', route: '/settings/preferences',
  },
  // 2ECOMP: PremiumUI Refresh — item idioma direto na sidebar (igual GPSWOX)
  {
    id: 'language', icon: LanguageIcon, labelKey: 'sharedLanguage', label: 'Idioma', action: 'language',
  },
  {
    id: 'account', icon: AccountCircleOutlined, labelKey: 'settingsUser', label: 'Conta', action: 'account',
  },
];

const useStyles = makeStyles()((theme) => {
  const isDark = theme.palette.mode === 'dark';

  return {
    // 2ECOMP: PremiumUI Refresh — root com transição de largura
    root: {
      position: 'fixed',
      left: 0,
      top: 0,
      bottom: 0,
      zIndex: Z_INDEX.ICON_BAR,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: isDark
        ? 'rgba(15, 17, 22, 0.98)'
        : 'rgba(255, 255, 255, 0.98)',
      backdropFilter: 'blur(8px)',
      overflowX: 'hidden',
      transition: SIDEBAR_TRANSITION,
      // 2ECOMP: PremiumUI Refresh — shadow GPSWOX (sem borderRight, shadow separa)
      boxShadow: isDark
        ? '0 5px 15px rgba(0,0,0,0.3), 0 15px 35px rgba(0,0,0,0.25), 0 2px 9px rgba(0,0,0,0.35)'
        : '0 5px 15px rgba(0,0,0,0.08), 0 15px 35px rgba(60,66,87,0.12), 0 2px 9px rgba(0,0,0,0.15)',
    },

    // 2ECOMP: PremiumUI Refresh — header com logo + hamburger
    header: {
      display: 'flex',
      alignItems: 'center',
      height: 48,
      borderBottom: `1px solid ${theme.palette.divider}`,
      flexShrink: 0,
      gap: 8,
    },

    // 2ECOMP: PremiumUI Refresh — botão collapse (hamburger)
    collapseBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 32,
      height: 32,
      borderRadius: 8,
      cursor: 'pointer',
      flexShrink: 0,
      color: theme.palette.text.secondary,
      transition: SELECTION_TRANSITION,
      '&:hover': {
        backgroundColor: 'rgba(0, 212, 170, 0.10)',
        color: '#00d4aa',
      },
    },

    // 2ECOMP: PremiumUI Refresh — lista de itens
    navItems: {
      display: 'flex',
      flexDirection: 'column',
      padding: '4px 0',
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
    },

    // 2ECOMP: PremiumUI Refresh — item de navegação (ícone + texto)
    navItem: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.25),
      padding: '8px 14px',
      cursor: 'pointer',
      color: theme.palette.text.secondary,
      transition: SELECTION_TRANSITION,
      userSelect: 'none',
      '&:hover': {
        backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
        color: theme.palette.text.primary,
      },
      '&:focus-visible': {
        outline: '2px solid #00d4aa',
        outlineOffset: -2,
      },
    },
    // 2ECOMP: PremiumUI Refresh — item colapsado (só ícone, centralizado)
    navItemCollapsed: {
      justifyContent: 'center',
      padding: '10px 0',
      gap: 0,
    },
    navItemActive: {
      backgroundColor: 'rgba(0, 212, 170, 0.12)',
      color: '#00d4aa',
      '&:hover': {
        backgroundColor: 'rgba(0, 212, 170, 0.18)',
      },
    },
    navLabel: {
      fontSize: '0.78rem',
      fontWeight: 400,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      flex: 1,
      lineHeight: 1.3,
    },

    // 2ECOMP: PremiumUI Refresh — seção inferior
    bottomItems: {
      display: 'flex',
      flexDirection: 'column',
      borderTop: `1px solid ${theme.palette.divider}`,
      padding: '4px 0',
      flexShrink: 0,
    },
  };
});

const PremiumIconBar = ({
  onToggleList, listOpen, onCommandsClick, collapsed, onToggleCollapse,
}) => {
  const { classes, cx } = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const t = useTranslation();

  // 2ECOMP: PremiumUI Refresh — seletor de idioma
  const { languages, language, setLocalLanguage } = useLocalization();
  const user = useSelector((state) => state.session.user);

  // Account menu state
  const [accountAnchorEl, setAccountAnchorEl] = useState(null);
  // 2ECOMP: PremiumUI Refresh — modal de idioma com bandeiras
  const [langModalOpen, setLangModalOpen] = useState(false);
  const [langSearch, setLangSearch] = useState('');

  // 2ECOMP: PremiumUI Refresh — aplica idioma imediatamente (localStorage + user attributes)
  const handleSelectLanguage = async (code) => {
    setLocalLanguage(code);
    setLangModalOpen(false);
    setLangSearch('');
    // Atualizar user.attributes.language via API para persistir no servidor
    if (user?.id) {
      try {
        const response = await fetchOrThrow(`/api/users/${user.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...user, attributes: { ...user.attributes, language: code } }),
        });
        dispatch(sessionActions.updateUser(await response.json()));
      } catch {
        // localStorage já foi salvo, funciona offline
      }
    }
  };

  const handleItemClick = (item, event) => {
    if (item.action === 'toggle') {
      onToggleList();
    } else if (item.action === 'modal') {
      onCommandsClick?.();
    } else if (item.action === 'account') {
      setAccountAnchorEl(event.currentTarget);
    // 2ECOMP: PremiumUI Refresh — abrir modal de idioma direto
    } else if (item.action === 'language') {
      setLangModalOpen(true);
    } else if (item.route) {
      navigate(item.route);
    }
  };

  const isActive = (item) => {
    if (item.action === 'toggle') return listOpen;
    if (!item.route) return false;
    return location.pathname.startsWith(item.route);
  };

  const handleLogout = async () => {
    setAccountAnchorEl(null);
    const response = await fetch('/api/session', { method: 'DELETE' });
    if (response.ok) {
      dispatch(sessionActions.updateUser(null));
      navigate('/login');
    }
  };

  const getLabel = (item) => t(item.labelKey) || item.label;

  // 2ECOMP: PremiumUI Refresh — item idioma = bandeira + nome
  const currentLangData = languages[language];
  const currentCountry = currentLangData?.country || '';
  const currentLangName = currentLangData?.name || language;

  const renderLanguageItem = () => {
    const content = (
      <Box
        key="language"
        className={cx(classes.navItem, collapsed && classes.navItemCollapsed)}
        onClick={() => setLangModalOpen(true)}
        role="button"
        tabIndex={0}
        aria-label={currentLangName}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setLangModalOpen(true);
          }
        }}
      >
        {currentCountry ? (
          <ReactCountryFlag
            countryCode={currentCountry}
            svg
            style={{
              width: 20, height: 14, borderRadius: 2, objectFit: 'cover', flexShrink: 0,
              border: '1px solid rgba(128,128,128,0.2)',
            }}
          />
        ) : (
          <LanguageIcon sx={{ fontSize: collapsed ? 20 : 18, flexShrink: 0 }} />
        )}
        {/* 2ECOMP: PremiumUI Refresh — nome do idioma quando expandido */}
        {!collapsed && (
          <Typography className={classes.navLabel} component="span">
            {currentLangName}
          </Typography>
        )}
      </Box>
    );

    // Tooltip só quando colapsado (expandido já mostra o nome)
    if (collapsed) {
      return (
        <Tooltip key="language" title={currentLangName} placement="right" arrow>
          {content}
        </Tooltip>
      );
    }
    return content;
  };

  // 2ECOMP: PremiumUI Refresh — renderiza item com modo collapsed
  const renderNavItem = (item) => {
    // 2ECOMP: PremiumUI Refresh — item idioma tem render dedicado (só bandeira)
    if (item.id === 'language') return renderLanguageItem();

    const itemContent = (
      <Box
        key={item.id}
        className={cx(
          classes.navItem,
          collapsed && classes.navItemCollapsed,
          isActive(item) && classes.navItemActive,
        )}
        onClick={(e) => handleItemClick(item, e)}
        role="button"
        tabIndex={0}
        aria-label={getLabel(item)}
        aria-pressed={item.action === 'toggle' ? listOpen : undefined}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleItemClick(item, e);
          }
        }}
      >
        <item.icon sx={{ fontSize: collapsed ? 20 : 18, flexShrink: 0 }} />
        {!collapsed && (
          <Typography className={classes.navLabel} component="span">
            {getLabel(item)}
          </Typography>
        )}
      </Box>
    );

    // 2ECOMP: PremiumUI Refresh — Tooltip no modo colapsado
    if (collapsed) {
      return (
        <Tooltip key={item.id} title={getLabel(item)} placement="right" arrow>
          {itemContent}
        </Tooltip>
      );
    }
    return itemContent;
  };

  // 2ECOMP: PremiumUI Refresh — lista de idiomas com bandeiras
  const languageList = Object.entries(languages).map(([code, lang]) => ({
    code,
    country: lang?.country || '',
    name: typeof lang === 'string' ? lang : lang.name || code,
  }));

  const filteredLanguages = langSearch
    ? languageList.filter((lang) => lang.name.toLowerCase().includes(langSearch.toLowerCase())
      || lang.code.toLowerCase().includes(langSearch.toLowerCase()))
    : languageList;

  return (
    <Box
      className={classes.root}
      sx={{ width: collapsed ? ICON_BAR_COLLAPSED_WIDTH : ICON_BAR_WIDTH }}
      role="navigation"
      aria-label={t('mapNavigation') || 'Main navigation menu'}
    >
      {/* 2ECOMP: PremiumUI Refresh — header com logo + hamburger */}
      <Box
        className={classes.header}
        sx={{
          justifyContent: collapsed ? 'center' : 'space-between',
          padding: collapsed ? '0' : '0 8px 0 12px',
        }}
      >
        {!collapsed && (
          <LogoImage style={{
            height: 36, maxWidth: 120, objectFit: 'contain', flex: 1, minWidth: 0,
          }}
          />
        )}
        <Tooltip title={collapsed ? (t('sharedExpand') || 'Expand') : (t('sharedCollapse') || 'Collapse')} placement="right">
          <Box
            className={classes.collapseBtn}
            onClick={onToggleCollapse}
            role="button"
            tabIndex={0}
            aria-label={collapsed ? (t('sharedExpand') || 'Expand') : (t('sharedCollapse') || 'Collapse')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleCollapse(); } }}
          >
            {collapsed
              ? <MenuIcon sx={{ fontSize: 20 }} />
              : <MenuOpenIcon sx={{ fontSize: 20 }} />}
          </Box>
        </Tooltip>
      </Box>

      {/* 2ECOMP: PremiumUI Refresh — itens de navegação principal */}
      <Box className={classes.navItems}>
        {NAV_ITEMS.map(renderNavItem)}
      </Box>

      {/* 2ECOMP: PremiumUI Refresh — itens do rodapé */}
      <Box className={classes.bottomItems}>
        {BOTTOM_ITEMS.map(renderNavItem)}
      </Box>

      {/* 2ECOMP: PremiumUI Refresh — Account menu com idioma */}
      <Menu
        anchorEl={accountAnchorEl}
        open={Boolean(accountAnchorEl)}
        onClose={() => setAccountAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <MenuItem onClick={() => { setAccountAnchorEl(null); navigate('/settings/user'); }}>
          <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('settingsUser') || 'Perfil'}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setAccountAnchorEl(null); setLangModalOpen(true); }}>
          <ListItemIcon><LanguageIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('sharedLanguage') || 'Language'}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('loginLogout') || 'Sair'}</ListItemText>
        </MenuItem>
      </Menu>

      {/* 2ECOMP: PremiumUI Refresh — modal de idioma com bandeiras */}
      <Dialog
        open={langModalOpen}
        onClose={() => { setLangModalOpen(false); setLangSearch(''); }}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              maxHeight: '80vh',
            },
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LanguageIcon sx={{ fontSize: 22, color: '#00d4aa' }} />
            <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
              {t('sharedLanguage') || 'Language'}
            </Typography>
          </Box>
          <IconButton onClick={() => { setLangModalOpen(false); setLangSearch(''); }} size="small">
            <CloseOutlined fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t('sharedSearch') || 'Search...'}
            value={langSearch}
            onChange={(e) => setLangSearch(e.target.value)}
            autoFocus
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined sx={{ fontSize: 18, opacity: 0.5 }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ mb: 2 }}
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 1,
            }}
          >
            {filteredLanguages.map((lang) => {
              const isSelected = language === lang.code;
              return (
                <Box
                  key={lang.code}
                  onClick={() => handleSelectLanguage(lang.code)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelectLanguage(lang.code);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    cursor: 'pointer',
                    border: isSelected ? '1.5px solid #00d4aa' : '1.5px solid transparent',
                    backgroundColor: isSelected ? 'rgba(0, 212, 170, 0.08)' : 'transparent',
                    transition: SELECTION_TRANSITION,
                    '&:hover': {
                      backgroundColor: isSelected ? 'rgba(0, 212, 170, 0.14)' : 'action.hover',
                    },
                  }}
                >
                  {lang.country && (
                    <ReactCountryFlag
                      countryCode={lang.country}
                      svg
                      style={{ width: 22, height: 16, borderRadius: 2, objectFit: 'cover' }}
                    />
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      fontWeight: isSelected ? 600 : 400,
                      color: isSelected ? '#00d4aa' : 'text.primary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {lang.name}
                  </Typography>
                  {isSelected && <CheckIcon sx={{ fontSize: 16, color: '#00d4aa', flexShrink: 0 }} />}
                </Box>
              );
            })}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PremiumIconBar;
